/**
 * Screen — Walk-in history.
 *
 * "Recent walk-ins" on the Walk-in screen only shows today's; this is the
 * whole record, because two questions need more than a day of data: "which
 * class is bringing in the most drop-ins" and "who keeps walking in instead
 * of joining" (the visit badge — see `annotateWalkInVisits` in
 * @shared/walkins). No money total is shown — see the comment on Dashboard.tsx
 * about keeping takings off the counter monitor; per-row amounts are fine,
 * a summed figure is not.
 */
import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  countWalkInRowsByClass,
  filterWalkInRows,
  filterWalkInRowsByClass,
  filterWalkInRowsByRange,
  loadWalkInHistory,
  type WalkInHistoryRow,
} from '../db/queries';
import ClassFilter from '../components/ClassFilter';
import VoidWalkInDialog from '../components/VoidWalkInDialog';
import { classLabel } from '@shared/catalog';
import { formatEgp } from '@shared/money';
import {
  formatDisplayDate,
  formatTime,
  monthBounds,
  shiftMonths,
  todayISO,
  weekBounds,
} from '@shared/dates';
import { formatPhone } from '@shared/identity';
import { ordinal, walkInIdentityKey } from '@shared/walkins';
import { PAYMENT_METHOD_LABELS } from '@shared/types';

const ROW_CAP = 100;

type PresetKey = 'today' | 'week' | 'month' | 'lastMonth' | 'all';

function presetRange(key: PresetKey, today: string): { from: string; to: string } | null {
  switch (key) {
    case 'today':
      return { from: today, to: today };
    case 'week':
      return weekBounds(today);
    case 'month':
      return monthBounds(today);
    case 'lastMonth':
      return monthBounds(shiftMonths(today, -1));
    case 'all':
      return null; // no bound — every walk-in ever recorded
  }
}

export default function WalkInHistory() {
  const today = todayISO();
  const [preset, setPreset] = useState<PresetKey>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [classKey, setClassKey] = useState('all');
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(ROW_CAP);
  const [voidingFor, setVoidingFor] = useState<WalkInHistoryRow | null>(null);

  const rows = useLiveQuery(() => loadWalkInHistory(), [], undefined);

  // Custom dates override the preset the moment either is touched, same idea
  // as the portal's date-range inputs.
  const range = useMemo(() => {
    if (customFrom || customTo) {
      return { from: customFrom || '0000-01-01', to: customTo || today };
    }
    return presetRange(preset, today);
  }, [preset, customFrom, customTo, today]);

  const inRange = useMemo(() => {
    if (!rows) return [];
    return range ? filterWalkInRowsByRange(rows, range.from, range.to) : rows;
  }, [rows, range]);

  const classCounts = useMemo(() => countWalkInRowsByClass(inRange), [inRange]);

  const visible = useMemo(
    () => filterWalkInRows(filterWalkInRowsByClass(inRange, classKey), query),
    [inRange, classKey, query]
  );

  /** Sorted by count desc, only classes that actually appear — see countWalkInRowsByClass. */
  const byClass = useMemo(
    () =>
      Object.entries(classCounts)
        .map(([key, count]) => ({ key, count }))
        .sort((a, b) => b.count - a.count),
    [classCounts]
  );

  const distinctPeople = useMemo(
    () => new Set(visible.map((r) => walkInIdentityKey(r))).size,
    [visible]
  );
  // totalVisits is over the person's WHOLE history, not just what's in view —
  // deliberately: someone who dropped in twice last month and once this month
  // is still a repeat visitor when looking at "this month" alone.
  const repeatVisitors = useMemo(() => {
    const seen = new Set<string>();
    let repeats = 0;
    for (const r of visible) {
      const key = walkInIdentityKey(r);
      if (r.totalVisits >= 2 && !seen.has(key)) {
        seen.add(key);
        repeats++;
      }
    }
    return repeats;
  }, [visible]);

  function setPresetAndClearCustom(key: PresetKey) {
    setPreset(key);
    setCustomFrom('');
    setCustomTo('');
    setVisibleCount(ROW_CAP);
  }

  const PRESETS: { key: PresetKey; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This week' },
    { key: 'month', label: 'This month' },
    { key: 'lastMonth', label: 'Last month' },
    { key: 'all', label: 'All time' },
  ];

  return (
    <div className="mx-auto max-w-5xl px-5 py-6">
      <h1 className="mb-1 text-4xl text-brand">Walk-in history</h1>
      <p className="mb-5 text-white/40">Every drop-in, with the class they trained.</p>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPresetAndClearCustom(p.key)}
            className={`rounded-btn border-2 px-4 py-2 text-sm font-bold uppercase tracking-button
                        transition-all duration-brand ${
                          preset === p.key && !customFrom && !customTo
                            ? 'border-brand bg-brand text-paper'
                            : 'border-edge bg-surface text-white/60 hover:border-brand'
                        }`}
          >
            {p.label}
          </button>
        ))}
        <input
          type="date"
          value={customFrom}
          onChange={(e) => {
            setCustomFrom(e.target.value);
            setVisibleCount(ROW_CAP);
          }}
          className="field tabular w-auto py-2 text-sm"
        />
        <span className="text-white/30">→</span>
        <input
          type="date"
          value={customTo}
          onChange={(e) => {
            setCustomTo(e.target.value);
            setVisibleCount(ROW_CAP);
          }}
          className="field tabular w-auto py-2 text-sm"
        />
      </div>

      <input
        className="field mb-4"
        placeholder="Search name or phone…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setVisibleCount(ROW_CAP);
        }}
        dir="auto"
      />

      <ClassFilter
        value={classKey}
        onChange={(k) => {
          setClassKey(k);
          setVisibleCount(ROW_CAP);
        }}
        counts={classCounts}
        className="mb-5"
      />

      {rows === undefined ? (
        <p className="py-16 text-center text-white/30">Loading…</p>
      ) : (
        <>
          {/* Counts only — never a money total, see the header comment. */}
          <div className="mb-5 grid gap-3 sm:grid-cols-3">
            <SummaryTile label="Walk-ins in view" value={visible.length} />
            <SummaryTile label="Distinct people" value={distinctPeople} />
            <SummaryTile label="Repeat visitors" value={repeatVisitors} tone="text-gold" />
          </div>

          {byClass.length > 0 && (
            <section className="card mb-6 p-4">
              <h2 className="mb-3 text-sm font-bold uppercase tracking-button text-white/40">
                By class
              </h2>
              <div className="flex flex-wrap gap-2">
                {byClass.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => {
                      setClassKey(c.key);
                      setVisibleCount(ROW_CAP);
                    }}
                    className={`rounded-btn border px-3 py-1.5 text-xs font-semibold transition-all duration-brand ${
                      classKey === c.key
                        ? 'border-brand bg-brand text-paper'
                        : 'border-edge bg-surface text-white/55 hover:border-brand hover:text-paper'
                    }`}
                  >
                    {classLabel(c.key)}
                    <span className="tabular ml-1.5 opacity-60">{c.count}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {visible.length === 0 ? (
            <div className="card p-12 text-center">
              <p className="text-lg text-white/50">
                {inRange.length === 0 ? 'No walk-ins in this range' : 'Nobody matches that'}
              </p>
            </div>
          ) : (
            <>
              <ul className="space-y-2">
                {visible.slice(0, visibleCount).map((row) => (
                  <WalkInHistoryListRow
                    key={row.id}
                    row={row}
                    onVoid={() => setVoidingFor(row)}
                  />
                ))}
              </ul>

              {visible.length > visibleCount && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => setVisibleCount((c) => c + ROW_CAP)}
                    className="btn-ghost"
                  >
                    Show more ({visible.length - visibleCount} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {voidingFor && (
        <VoidWalkInDialog walkIn={voidingFor} onClose={() => setVoidingFor(null)} />
      )}
    </div>
  );
}

function SummaryTile({
  label,
  value,
  tone = 'text-brand',
}: {
  label: string;
  value: number;
  tone?: string;
}) {
  return (
    <div className="card p-4">
      <div className="text-[10px] font-bold uppercase tracking-button text-white/30">{label}</div>
      <div className={`tabular text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

function WalkInHistoryListRow({
  row,
  onVoid,
}: {
  row: WalkInHistoryRow;
  onVoid: () => void;
}) {
  return (
    <li className="card flex flex-wrap items-center gap-x-4 gap-y-2 p-4">
      <div className="min-w-[160px] flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold" dir="auto">
            {row.person_name}
          </span>
          {row.visitNumber >= 2 && (
            <span
              className="whitespace-nowrap rounded-btn border border-gold/40 bg-gold/10 px-2 py-0.5
                         text-[10px] font-bold uppercase tracking-button text-gold"
              title={`${row.totalVisits} visits on record`}
            >
              {ordinal(row.visitNumber)} visit
            </span>
          )}
        </div>
        <div className="tabular text-sm text-white/40">{formatPhone(row.phone)}</div>
      </div>

      {/* The class is the point of this screen — bigger and brighter than the rest of the row. */}
      <div className="min-w-[170px] flex-1">
        <div className="text-base font-semibold text-paper">{classLabel(row.class_key)}</div>
        <div className="text-sm text-white/40">
          {formatDisplayDate(row.visited_at)} · {formatTime(row.created_at)}
        </div>
      </div>

      <div className="min-w-[110px] text-sm text-white/40">
        {PAYMENT_METHOD_LABELS[row.method]}
        <div className="text-white/30">{row.received_by}</div>
      </div>

      <div className="tabular w-24 shrink-0 text-right text-sm font-bold text-brand">
        {formatEgp(row.amount_piastres, { hideZeroFraction: true })}
      </div>

      <button
        onClick={onVoid}
        className="min-h-[40px] shrink-0 rounded-btn border border-status-expired/40 px-4
                   text-xs font-bold uppercase tracking-button text-status-expired
                   transition-all duration-brand hover:bg-status-expiredBg"
      >
        Void
      </button>
    </li>
  );
}
